// The song half of the /create card. SongArea is a row of sources (upload,
// the library, type words, an AI song, record) over the chosen source's
// form; once a song is picked it collapses to one row with Change. Every
// source reports a song in the shape CreateModal stores, tagged with its
// sourceMode, so the request body is unchanged.
import React, { useEffect, useState } from "react";
import axios from "axios";
import Image from "next/image";
import { useQuery } from "react-query";
import { getIdToken } from "../../utils/firebaseAuth";
import {
  Disc3,
  Library,
  Mic,
  Search,
  Sparkles,
  Type,
  Upload,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthProvider";
import useAuthModal from "@/hooks/useAuthModal";
import UploadSongViewJs from "@/components/modals/UploadSongView";
import MicrophoneRecordViewJs from "@/components/modals/MicrophoneRecordView";
import { AiSong, TypeWords } from "./SongSources";

/* eslint-disable @typescript-eslint/no-explicit-any */

// Untyped JS views; their props are all optional in practice.
const UploadSongView = UploadSongViewJs as unknown as React.FC<any>;
const MicrophoneRecordView = MicrophoneRecordViewJs as unknown as React.FC<any>;

export type SourceMode = "upload" | "library" | "mic" | "tts" | "generate";

// Same labels as CreateModal's SOURCE_TYPE_LABEL.
export const SOURCE_TYPE_LABEL: Record<string, string> = {
  generate: "AI Generated",
  upload: "Uploaded",
  library: "From Library",
  mic: "Recorded",
  tts: "Text to Speech",
};

export const formatLength = (seconds?: number | null) => {
  if (typeof seconds !== "number" || !isFinite(seconds) || seconds <= 0)
    return null;
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export const songLengthSeconds = (song: any): number | null => {
  const d = song?.duration ?? song?.metadata?.duration;
  return typeof d === "number" ? d : null;
};

// The details objects components/conversion/Song.js and RecentFile.js build,
// so a pick here submits exactly what the old library browser would.
const libraryDetails = (song: any) => ({
  url: song.urls?.base || song.urls?.vocals_clean || song.urls?.instrumentals,
  title: song.metadata?.title || song.title || "Untitled Song",
  metadata: song.metadata,
  _id: song._id,
  duration: song.duration,
  sourceMode: "library" as SourceMode,
});
const uploadDetails = (file: any) => ({
  url: file.urls?.base,
  title: file.metadata?.title || file.fileName || "Untitled File",
  metadata: file.metadata,
  _id: file._id,
  sourceMode: "library" as SourceMode,
});

const SongRow = ({
  art,
  title,
  sub,
  onClick,
  disabled,
}: {
  art?: string;
  title: string;
  sub?: string;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="tw-flex tw-w-full tw-min-w-0 tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-[20px] tw-border-0 tw-bg-transparent tw-p-2 tw-text-left tw-text-jam-ink hover:tw-bg-jam-lav disabled:tw-opacity-40"
  >
    <span className="tw-relative tw-flex tw-h-12 tw-w-12 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-[14px] tw-bg-jam-lav">
      {art ? (
        <Image src={art} alt="" fill sizes="48px" className="tw-object-cover" />
      ) : (
        <Disc3 size={20} className="tw-text-jam-purple" />
      )}
    </span>
    <span className="tw-min-w-0 tw-flex-grow">
      <span className="tw-block tw-truncate tw-text-[15px] tw-font-extrabold">
        {title}
      </span>
      {sub && (
        <span className="tw-block tw-truncate tw-text-xs tw-font-bold tw-text-jam-muted">
          {sub}
        </span>
      )}
    </span>
  </button>
);

export const SongPicker = ({ onPick }: { onPick: (song: any) => void }) => {
  const { user } = useAuth();
  const [tab, setTab] = useState<"library" | "mine">("library");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data: rows = [], isFetching } = useQuery(
    ["studio-songs", tab, debounced, !!user],
    async () => {
      if (tab === "mine") {
        const token = await getIdToken();
        if (!token) return [];
        const { data } = await axios.get("/api/files/getRecentUploadedFiles", {
          headers: { Authorization: token },
          params: { page: 0, limit: 30, search: debounced || undefined },
        });
        return (data?.files ?? []).map((f: any) => ({
          key: f._id,
          art: f.metadata?.image?.url,
          sub: SOURCE_TYPE_LABEL.upload,
          details: uploadDetails(f),
        }));
      }
      const { data } = await axios.get("/api/library/getLibrarySongs", {
        params: { page: 0, limit: 30, search: debounced || undefined },
      });
      return (data?.songs ?? []).map((s: any) => ({
        key: s._id,
        art: s.metadata?.image?.url,
        sub: [s.metadata?.artist, formatLength(s.duration)]
          .filter(Boolean)
          .join(" · "),
        details: libraryDetails(s),
      }));
    },
    { keepPreviousData: true, staleTime: 60 * 1000 },
  );

  return (
    <>
      <label className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-full tw-bg-jam-lav tw-px-5 tw-py-3.5">
        <Search
          size={18}
          strokeWidth={2.5}
          className="tw-shrink-0 tw-text-jam-muted"
        />
        <input
          aria-label="Search songs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            tab === "mine" ? "Search your uploads" : "Search the song library"
          }
          className="tw-w-full tw-border-0 tw-bg-transparent tw-text-[15px] tw-font-bold tw-text-jam-ink tw-outline-none placeholder:tw-text-jam-muted"
        />
        {search && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => setSearch("")}
            className="tw-flex tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-jam-muted"
          >
            <X size={18} />
          </button>
        )}
      </label>

      {user && (
        <div className="tw-flex tw-gap-2">
          {(
            [
              ["library", "Popular"],
              ["mine", "Your uploads"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`tw-cursor-pointer tw-rounded-full tw-border-0 tw-px-3.5 tw-py-[7px] tw-text-[13px] tw-font-extrabold ${
                tab === id
                  ? "tw-bg-jam-ink tw-text-white"
                  : "tw-bg-jam-lav tw-text-jam-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {isFetching && rows.length === 0 ? (
        <div className="tw-flex tw-flex-col tw-gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="tw-h-16 tw-animate-pulse tw-rounded-[20px] tw-bg-jam-lav"
            />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="tw-rounded-[22px] tw-bg-jam-lav tw-p-7 tw-text-center tw-text-sm tw-font-bold tw-text-jam-muted">
          {debounced
            ? "No songs match that search."
            : tab === "mine"
              ? "You haven't uploaded anything yet."
              : "Nothing here yet."}
        </div>
      ) : (
        <div
          className={`tw-flex tw-max-h-[360px] tw-flex-col tw-overflow-y-auto ${isFetching ? "tw-opacity-60" : ""}`}
        >
          {rows.map((r: any) => (
            <SongRow
              key={r.key}
              art={r.art}
              title={r.details.title}
              sub={r.sub}
              disabled={!r.details.url}
              onClick={() => onPick(r.details)}
            />
          ))}
        </div>
      )}
    </>
  );
};

// TTS and generated songs carry a machine title ("Generate: ...") that is
// sent as-is; show the words or the style instead.
const displayTitle = (song: any) => {
  if (song.sourceMode === "generate" && song.metadata?.caption) {
    return song.metadata.caption;
  }
  if (song.sourceMode === "tts" && song.metadata?.text) {
    return `“${song.metadata.text}”`;
  }
  return song.title;
};

export const PickedSong = ({
  song,
  onChange,
}: {
  song: any;
  onChange: () => void;
}) => {
  const length = formatLength(songLengthSeconds(song));
  const art = song?.metadata?.image?.url;
  return (
    <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-[26px] tw-bg-white tw-p-2.5 tw-pr-3">
      <div className="tw-relative tw-flex tw-h-14 tw-w-14 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-[18px] tw-bg-jam-lav">
        {art ? (
          // Upload art is a blob: URL from the file's tags, so no next/image.
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={art}
            alt=""
            className="tw-h-full tw-w-full tw-object-cover"
          />
        ) : (
          <Disc3 size={24} className="tw-text-jam-purple" />
        )}
      </div>
      <div className="tw-min-w-0 tw-flex-grow">
        <div className="tw-truncate tw-text-[16px] tw-font-extrabold">
          {displayTitle(song)}
        </div>
        <div className="tw-text-xs tw-font-bold tw-text-jam-muted">
          {SOURCE_TYPE_LABEL[song.sourceMode] || "Picked"}
          {length ? ` · ${length}` : ""}
        </div>
      </div>
      <button
        type="button"
        onClick={onChange}
        className="tw-shrink-0 tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-4 tw-py-2.5 tw-text-[13px] tw-font-extrabold tw-text-jam-purple"
      >
        Change
      </button>
    </div>
  );
};

type Tab = "upload" | "library" | "tts" | "generate" | "mic";

export const SongArea = ({
  selectedVoices,
  song,
  onPick,
  onClear,
}: {
  selectedVoices: any[];
  song: any | null;
  onPick: (song: any) => void;
  onClear: () => void;
}) => {
  const { checkAuthAndExecute } = useAuthModal();
  const [tab, setTab] = useState<Tab>("upload");

  // Same Generate gate as CreateModal: hidden in the UK (resolved from edge
  // headers by /api/whoami) and when any picked voice is tagged `music`.
  const [isUK, setIsUK] = useState(false);
  useEffect(() => {
    let alive = true;
    fetch("/api/whoami")
      .then((r) => r.json())
      .then((d) => {
        if (alive) setIsUK(!!d?.isUK);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  const generateAvailable =
    !isUK &&
    selectedVoices.every(
      (v) => !(Array.isArray(v.badges) && v.badges.includes("music")),
    );
  const active: Tab = tab === "generate" && !generateAvailable ? "upload" : tab;

  const pick = (sourceMode: SourceMode) => (details: any) =>
    onPick({ ...details, sourceMode });
  // Upload and record store the file under the user's account. Logged-out
  // visitors use them as normal; at that moment the login modal opens and
  // the upload carries on after sign-in.
  const needsAuth = (retry: () => void) =>
    checkAuthAndExecute(retry, "Sign up to use your audio");

  if (song) {
    return (
      <PickedSong
        song={song}
        onChange={() => {
          // Reopen the source it came from (an upload or a library pick
          // both reopen on their own tab).
          const from = song.sourceMode as Tab;
          if (["upload", "library", "tts", "generate", "mic"].includes(from)) {
            setTab(from);
          }
          onClear();
        }}
      />
    );
  }

  const tabs: { id: Tab; label: string; Icon: React.ComponentType<any> }[] = [
    { id: "upload", label: "Upload", Icon: Upload },
    { id: "library", label: "Library", Icon: Library },
    { id: "tts", label: "Type words", Icon: Type },
    ...(generateAvailable
      ? [{ id: "generate" as Tab, label: "AI song", Icon: Sparkles }]
      : []),
    { id: "mic", label: "Record", Icon: Mic },
  ];

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-[24px] tw-bg-white tw-p-3 md:tw-p-4">
      <div
        role="tablist"
        aria-label="Where the song comes from"
        className="tw-flex tw-gap-1 tw-overflow-x-auto tw-rounded-full tw-bg-jam-lav tw-p-1 [&::-webkit-scrollbar]:tw-hidden"
      >
        {tabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={active === id}
            onClick={() => setTab(id)}
            className={`tw-flex tw-flex-1 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-border-0 tw-px-3 tw-py-2.5 tw-text-[13px] tw-font-extrabold tw-transition-colors ${
              active === id
                ? "tw-bg-white tw-text-jam-purple"
                : "tw-bg-transparent tw-text-jam-ink hover:tw-text-jam-purple"
            }`}
          >
            <Icon size={15} strokeWidth={2.5} />
            {label}
          </button>
        ))}
      </div>

      {active === "upload" && (
        <>
          <UploadSongView
            variant="jam"
            initialModel={selectedVoices[0]}
            onSongSelected={pick("upload")}
            onNeedsAuth={needsAuth}
            onError={(msg: string, err: unknown) =>
              console.error("Upload Error:", msg, err)
            }
          />
          <p className="tw-m-0 tw-text-center tw-text-xs tw-font-bold tw-text-jam-muted">
            Only upload audio you have the rights to use.
          </p>
        </>
      )}
      {active === "library" && <SongPicker onPick={onPick} />}
      {active === "tts" && (
        <TypeWords
          selectedVoices={selectedVoices}
          onConfirm={(ttsData) =>
            pick("tts")({
              url: null, // No direct audio URL yet
              title: `TTS: "${ttsData.text.substring(0, 30).trim()}...";`,
              metadata: { source: "tts", ...ttsData },
            })
          }
        />
      )}
      {active === "generate" && (
        <AiSong
          voiceModelName={selectedVoices[0]?.urlParam?.replace(/-/g, " ") || ""}
          onConfirm={(generateData) =>
            pick("generate")({
              url: null, // Generated at submit time, then voice-converted
              title: `Generate: "${generateData.caption.substring(0, 30).trim()}..."`,
              metadata: { source: "generate", ...generateData },
            })
          }
        />
      )}
      {active === "mic" && (
        <MicrophoneRecordView
          variant="jam"
          onSongSelected={pick("mic")}
          onNeedsAuth={needsAuth}
          onError={(msg: string, err: unknown) =>
            console.error("Record Error:", msg, err)
          }
        />
      )}
    </div>
  );
};
